import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import getSlug from 'speakingurl';
import screenfull from 'screenfull';

// Import hooks
import { useAppSelector } from 'store/hooks';

// Import styles
import { media, flex } from 'components/styles';

// Import components
import { useContextProvider } from 'components/views/vod/components/PlayerWrapper/useContextProvider';
import { Button, PlayerElements as Player } from 'components/elements';

// Import variables
import { ROUTES_CORE_PATHS } from 'helpers/variables';

// Import helpers
import { getTypeTranslation } from 'helpers';
import { useUserAgent } from 'components/utilities';

const { TRANSACTION } = ROUTES_CORE_PATHS;

const TopHeadline = React.memo(
	({ isFocus, isError }) => {
		const { pathname: currentPath } = useLocation();
		const history = useHistory();
		const { isMobile } = useUserAgent();

		const {
			metadata: { title },
			uuid
		} = useAppSelector(({ movieDetails }) => movieDetails.data);

		const { previousPath } = useAppSelector(({ previous_location }) => previous_location);

		const { setFullScreen, isReady, isPreview, isFullScreen, closePreview } = useContextProvider();

		const isFromTransaction = previousPath.includes(TRANSACTION) || currentPath === previousPath;

		const seriesTranslation = getTypeTranslation('series');

		const isEpisode = currentPath.includes(seriesTranslation);
		const type = isEpisode ? 'series' : 'movies';

		const subtypeTranslated = getTypeTranslation(type);
		const slugTitle = getSlug(title);

		const redirectUrl = `/vod/${subtypeTranslated}/${uuid}/${slugTitle}`;

		const handleGoBack = () => {
			if (isPreview) {
				closePreview();
			} else {
				isFromTransaction ? history.push(redirectUrl) : history.goBack();
			}
		};

		return (
			<Wrapper isFocus={isFocus || isError}>
				<Heading onClick={handleGoBack} as={isMobile && 'span'}>
					{title}
				</Heading>
				<Controller>
					{!isPreview && screenfull.isEnabled && (
						<ButtonFullScreen onClick={setFullScreen} modifiers={['icon', 'playerIconHover']} disabled={isError || !isReady}>
							{isFullScreen ? <Player.IconExitFullScreen /> : <Player.IconFullScreen />}
						</ButtonFullScreen>
					)}

					<Button onClick={handleGoBack} modifiers={['icon', 'playerIconHover']}>
						<Player.IconCloseThin />
					</Button>
				</Controller>
			</Wrapper>
		);
	},
	(prev, next) => JSON.stringify(prev) === JSON.stringify(next)
);

const Wrapper = styled.div`
	position: absolute;
	width: 100%;
	top: 0;
	left: 0;
	padding: 3rem 2rem;
	${flex({ jc: 'space-between' })}
	flex-wrap: wrap-reverse;
	column-gap: 2rem;
	${media.min.xmin(css`
		padding: 5rem 4rem;
	`)}
	z-index: 99;
	opacity: ${({ isFocus }) => (isFocus ? 1 : 0)};
	visibility: ${({ isFocus }) => (isFocus ? 'visible' : 'hidden')};
	transition: all 0.3s ease-in-out;
`;

const Heading = styled(Button)`
	color: ${({ theme }) => theme.white};
	font-weight: ${({ theme }) => theme.font.light};
	font-size: 2.6rem !important;
	font-family: 'Lato', sans-serif;
	opacity: 0.7;
	padding: 0;
	word-wrap: break-word;
	display: inline-flex;

	&:hover {
		opacity: 1;
	}
	${media.min.xmin(css`
		margin-bottom: 1.8rem;
		margin-top: 0;
	`)}
`;

const Controller = styled.div`
	display: flex;
	align-items: center;
`;

const ButtonFullScreen = styled(Button)`
	margin-right: 3rem;
`;

TopHeadline.propTypes = {
	isFocus: PropTypes.bool.isRequired,
	isError: PropTypes.bool.isRequired
};

export default TopHeadline;
